import { useState, useEffect, useRef } from 'react';
import PIOPIY from 'piopiyjs';

const useCallManager = () => {
  // Separate states for shipper and consignee
  const [shipperCall, setShipperCall] = useState({
    status: 'disconnected',
    duration: 0,
    callId: null
  });
  const [consigneeCall, setConsigneeCall] = useState({
    status: 'disconnected',
    duration: 0,
    callId: null
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const piopiyRef = useRef(null);
  const timerRef = useRef(null);
  const activeCallType = useRef(null); // 'shipper' or 'consignee'

  // Initialize PIOPIY
  const initPIOPIY = async (userId, password) => {
    try {
      // Clear any existing instance
      if (piopiyRef.current) {
        try {
          await piopiyRef.current.logout();
        } catch (logoutErr) {
          console.warn("Logout error:", logoutErr);
        }
        piopiyRef.current = null;
      }

      // Reset all call states
      resetCallStates();
      setLoginError(null);

      // Create new instance
      piopiyRef.current = new PIOPIY({
        name: 'Admin',
        debug: true,
        autoplay: true,
        ringTime: 60
      });

      // Event handlers
      piopiyRef.current.on('login', (res) => {
        if (res.code === 200) {
          setIsInitialized(true);
          setLoginError(null);
        }
      });

      piopiyRef.current.on('loginFailed', () => {
        setIsInitialized(false);
        setLoginError('Login Failed. Please contact support team.');
        resetCallStates();
      });

      piopiyRef.current.on('trying', () => {
        if (activeCallType.current) {
          updateCallStatus(activeCallType.current, 'calling');
        }
      });

      piopiyRef.current.on('ringing', () => {
        if (activeCallType.current) {
          updateCallStatus(activeCallType.current, 'ringing');
        }
      });

      piopiyRef.current.on('answered', () => {
        if (activeCallType.current) {
          updateCallStatus(activeCallType.current, 'connected');
          startCallTimer();
        }
      });

      piopiyRef.current.on('hangup', () => handleCallEnd());
      piopiyRef.current.on('ended', () => handleCallEnd());

      piopiyRef.current.on('error', (err) => {
        console.error('Call error:', err);
        if (activeCallType.current) {
          updateCallStatus(activeCallType.current, 'error');
        }
        setLoginError('Call error occurred. Please try again.');
      });

      // Login with credentials
      piopiyRef.current.login(userId, password, 'sbcind.telecmi.com');
    } catch (error) {
      console.error('PIOPIY initialization error:', error);
      setIsInitialized(false);
      setLoginError('Initialization failed. Please contact support.');
    }
  };

  const resetCallStates = () => {
    setShipperCall({
      status: 'disconnected',
      duration: 0,
      callId: null
    });
    setConsigneeCall({
      status: 'disconnected',
      duration: 0,
      callId: null
    });
    activeCallType.current = null;
    clearInterval(timerRef.current);
  };

  const updateCallStatus = (callType, status) => {
    if (callType === 'shipper') {
      setShipperCall(prev => ({
        ...prev,
        status
      }));
    } else if (callType === 'consignee') {
      setConsigneeCall(prev => ({
        ...prev,
        status
      }));
    }
  };

  const startCallTimer = () => {
    let seconds = 0;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      seconds++;
      if (activeCallType.current === 'shipper') {
        setShipperCall(prev => ({
          ...prev,
          duration: seconds
        }));
      } else if (activeCallType.current === 'consignee') {
        setConsigneeCall(prev => ({
          ...prev,
          duration: seconds
        }));
      }
    }, 1000);
  };

  const handleCallEnd = () => {
    clearInterval(timerRef.current);
    if (activeCallType.current) {
      updateCallStatus(activeCallType.current, 'ended');
      activeCallType.current = null;
    }
  };

  const makeCall = (number, callType) => {
    if (piopiyRef.current && isInitialized && !activeCallType.current) {
      try {
        const formattedNumber = formatPhoneNumber(number);
        piopiyRef.current.call(formattedNumber);
        
        // Set active call type
        activeCallType.current = callType;
        
        // Update call state
        if (callType === 'shipper') {
          setShipperCall({
            status: 'calling',
            duration: 0,
            callId: piopiyRef.current.getCallId()
          });
        } else {
          setConsigneeCall({
            status: 'calling',
            duration: 0,
            callId: piopiyRef.current.getCallId()
          });
        }
      } catch (error) {
        console.error('Call initiation error:', error);
        updateCallStatus(callType, 'call_failed');
        setLoginError('Call failed to initiate. Please try again.');
      }
    }
  };

  const endCall = () => {
    try {
      if (piopiyRef.current && activeCallType.current) {
        // piopiyRef.current.hangup();
        piopiyRef.current.logout();
      }
    } catch (error) {
      console.error('Error ending call:', error);
    }
  };

  const formatPhoneNumber = (number) => {
    const cleaned = number.replace(/\D/g, '');
    return cleaned.length > 10 ? cleaned : `91${cleaned}`;
  };

  useEffect(() => {
    return () => {
      if (piopiyRef.current) {
        try {
          // piopiyRef.current.hangup();
          piopiyRef.current.logout();
        } catch (error) {
          console.error('Cleanup error:', error);
        }
      }
      clearInterval(timerRef.current);
    };
  }, []);

  return {
    shipperCall,
    consigneeCall,
    isInitialized,
    loginError,
    initPIOPIY,
    makeCall,
    endCall
  };
};

export default useCallManager;